/**
 * The solo run's between-screens hub at `/solo/:runId`: the phases with no Decide screen. Resolving shows "Running FY…"; a halted
 * event shows the takeover sheet then "Continue the year"; a released round shows the result and the advance control (P9 solo).
 * `deciding` and `completed` hand straight on to their screens.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ErrorBody, EventForTeam, SoloRunState } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import { useRoute, failureOf } from "../api/context";
import { useSoloAdvance } from "./useSoloAdvance";
import type { RouteCall } from "../api/types";
import { answerMoreLine, othersLeft, positionIn, sameSitting } from "../events/deck";
import { limitsOf } from "../decisions/limits";
import { EventCard } from "../events/EventCard";
import { fiscalYearLabel, formatRate } from "../format";
import { BAND_LABELS } from "../format/labels";
import { changedDecisionIds } from "../results/derive";
import { useResultsData } from "../results/data";
import { ResultsPage } from "../results/ResultsPage";
import { useCohortData, type CohortData } from "../shell/data";
import { navigate } from "../shell/history";
import { GxErrorBanner, GxWaiting } from "../theme/primitives";
import { GxLoading } from "../theme/GxLoading";
import { GxStatusCard } from "../theme/GxStatusCard";
import { playPath } from "./run";

const POLL_MS = 2_500;

export function SoloRunPage({ runId, api }: { readonly runId: string; readonly api: ApiClient }) {
  const queryClient = useQueryClient();
  const run = useRoute("soloResume", { runId });
  // Only what was fetched on this visit: the cache may still hold the phase the player just left (deciding before Submit, awaiting_event before Continue).
  const state = run.isFetchedAfterMount ? run.data : undefined;
  // The live stream is Frame's (LiveUpdates covers /solo/:runId): a second one here would open two on one channel.
  const data = useCohortData(runId, state?.teamId ?? null, 1);
  const phase = state?.phase;
  // The year's results are released as the run reaches `reviewing`, and the halting month's cards are delivered at `awaiting_event` (the first card's "Card 1 of 2" needs
  // its sitting's other cards): what the hub loaded earlier is stale by then.
  useEffect(() => {
    if (phase === "reviewing") void queryClient.invalidateQueries({ predicate: (q) => ["teamState", "publicMarket", "roundReport"].includes(String(q.queryKey[0])) });
    // The top pill and the guide read the team's state: it is read again as the year resumes, not only when it halts or ends.
    if (phase === "awaiting_event" || phase === "resolving") void queryClient.invalidateQueries({ queryKey: ["teamState"] });
  }, [phase, queryClient]);

  useEffect(() => {
    if (state === undefined || phase === "deciding" || phase === "completed") { if (state !== undefined) navigate(playPath(state), { replace: true }); return; }
    if (phase !== "resolving") return;
    const timer = setInterval(() => { void queryClient.invalidateQueries({ queryKey: ["soloResume"] }); }, POLL_MS);
    return () => clearInterval(timer);
  }, [state, phase, queryClient]);

  if (run.error !== null) return <><GxErrorBanner error={failureOf(run.error)} /></>;
  if (state === undefined || data.status === "loading" || data.status === "no-team") return <GxWaiting what="your run" height={260} />;
  if (data.status === "error") return <><GxErrorBanner error={data.error} /></>;

  return (
    <>
      <div className="gx-solo-hub" data-testid={`solo.hub.${state.phase}`}>
        {state.phase === "resolving" ? <Resolving data={data} state={state} /> : null}
        {state.phase === "awaiting_event" && state.awaitingEvent !== null ? <Halted key={state.awaitingEvent.eventInstanceId} api={api} data={data} state={state} event={state.awaitingEvent} /> : null}
        {state.phase === "reviewing" ? <Reviewed api={api} data={data} state={state} /> : null}
      </div>
    </>
  );
}

type Ready = Extract<CohortData, { status: "ready" }>;

/** The plan the player committed, in three plain rows: how many decisions changed, the forecast, the thesis. */
function submittedRows(data: Ready): readonly { readonly key: string; readonly label: string; readonly text: string; readonly quote?: boolean }[] {
  const { team, catalog } = data;
  const draft = team.draft;
  const open = draft?.editableDecisionIds.length ?? catalog.decisions.length;
  const changed = draft === null ? 0 : changedDecisionIds(catalog, draft).size;
  const call = team.forecast?.state === "committed" ? team.forecast.forecast : null;
  const forecast = call === null ? "No forecast this year: the first year has none." : call.format === "band"
    ? `Net interest margin ${BAND_LABELS[call.nim].toLowerCase()}, loans written off ${BAND_LABELS[call.ncoRate].toLowerCase()}, Marked CET1 ${BAND_LABELS[call.cet1Marked].toLowerCase()}`
    : `Net interest margin ${formatRate(call.nim, 2)}, loans written off ${formatRate(call.ncoRate, 2)}, Marked CET1 ${formatRate(call.cet1Marked, 2)}`;
  const thesis = (draft?.thesis.text ?? "").trim();
  return [
    { key: "decisions", label: "Your decisions", text: draft === null ? "Your plan is saved." : `${changed} of ${open} changed, ${open - changed} maintained` },
    { key: "forecast", label: "Your forecasts", text: forecast },
    ...(thesis === "" ? [] : [{ key: "thesis", label: "Your thesis line", text: thesis, quote: true }]),
  ];
}

const STILL_WORKING_AFTER_MS = 20_000;

/** Whole seconds since `since`, once the year has run past the quiet period; null before it. Ticks every second. */
function useStillWorking(since: number): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  const elapsed = now - since;
  return elapsed >= STILL_WORKING_AFTER_MS ? Math.round(elapsed / 1_000) : null;
}

function Resolving({ data, state }: { readonly data: Ready; readonly state: SoloRunState }) {
  const fy = fiscalYearLabel(data.catalog, state.round.roundNo);
  const at = data.team.latestSubmission?.submittedAt;
  // Counted from this page opening, not from the submission: a year resumed after a card was submitted minutes ago.
  const [mountedAt] = useState(() => Date.now());
  const seconds = useStillWorking(mountedAt);
  const when = at === undefined ? null : new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  // One status card (U1-6): the page title says the state; the card holds the progress, the plan as submitted and the one sentence on what happens next.
  return (
    <div className="gx-run" data-testid="solo.resolving">
      <div className="gx-eyebrow">Round {state.round.roundNo} of {data.team.cohort.roundCount}</div>
      <h1 className="gx-h1">{fy} is running</h1>
      <GxStatusCard icon="clock" testId="solo.resolving.card" title={`Playing ${fy} for your bank, your twin and five other banks`}
        detail={<>
          {/* The api sends a player no quarter-by-quarter progress, so the bar is the indeterminate one. */}
          <div className="gx-run-bar" role="progressbar" aria-label={`Playing ${fy}`} data-testid="solo.resolving.bar"><i /></div>
          <p className="gx-run-label" role="status" data-testid="solo.resolving.progress">Running the year</p>
          {seconds === null ? null : <p className="gx-small gx-muted" role="status" data-testid="solo.resolving.elapsed">Still working. It has been about {seconds} seconds.</p>}
          <dl className="gx-run-rows" data-testid="solo.resolving.submitted">
            {submittedRows(data).map((r) => (
              <div key={r.key} className="gx-run-row" data-testid={`solo.resolving.${r.key}`}><dt>{r.label}</dt><dd className={r.quote === true ? "gx-run-quote" : undefined}>{r.text}</dd></div>
            ))}
          </dl>
          {when === null ? null : <p className="gx-small gx-faint">Submitted at {when}. There is nothing to edit now.</p>}
        </>}>
        Results open on this page. If a card arrives mid-year, the year pauses and asks you to answer. You can leave and come back; your run keeps its place.
      </GxStatusCard>
    </div>
  );
}

function Halted({ api, data, state, event }: { readonly api: ApiClient; readonly data: Ready; readonly state: SoloRunState; readonly event: EventForTeam }) {
  const queryClient = useQueryClient();
  // The options the memo reads as a supervisory risk, from the same context the Decide preview runs on; without it the card simply shows none.
  const context = useRoute("getPreviewContext", { teamId: data.team.team.id, roundId: state.round.id });
  const [answered, setAnswered] = useState(event.response !== null);
  const [error, setError] = useState<ErrorBody["error"] | null>(null);
  const respond: RouteCall<"respondToEvent"> = async (params, body) => {
    const r = await api.call("respondToEvent", params, body);
    if (r.ok) { setAnswered(true); setError(null); void queryClient.invalidateQueries({ predicate: (q) => ["soloResume", "teamState"].includes(String(q.queryKey[0])) }); } // the next halted card, if any, replaces this one
    return r;
  };
  const advanceRoute = api.route("soloAdvance");
  const { pending: resuming, go } = useSoloAdvance(state.runId, advanceRoute);
  const proceed = async () => {
    const r = await go({ action: "continue", roundId: state.round.id }, (next) => navigate(playPath(next)));
    if (!r.ok) setError(r.error);
  };
  const sitting = sameSitting(data.team.pendingEvents, event);
  // This card counts from its own answer, whether or not the team state lists it; the rest of its sitting from theirs.
  const others = othersLeft(sitting, event);
  // The cards of this sitting already answered, said once before the next one appears.
  const earlier = sitting.filter((e) => e.injectionId !== event.injectionId && e.response !== null);
  const left = others + (answered ? 0 : 1);
  const why = answerMoreLine(others, !answered);
  return (
    <div className="gx-solo-card-page" data-testid="solo.takeover">
      {error === null ? null : <GxErrorBanner {...(error.code === "EVENT_RESPONSE_PENDING" ? { message: "An event is waiting for your response." } : { error })} />}
      <div className="gx-eyebrow">Round {state.round.roundNo} of {data.team.cohort.roundCount}</div>
      <h1 className="gx-h1">{answered && left === 0 ? "You answered the card" : `${fiscalYearLabel(data.catalog, state.round.roundNo)} is paused for a card`}</h1>
      {answered && left === 0 ? <h2 className="gx-h2">Answered. Continue the year when you are ready.</h2> : null}
      {earlier.map((e) => (
        <p key={e.injectionId} className="gx-small gx-muted" role="status" data-testid="solo.answered-strip">
          You answered card {sitting.indexOf(e) + 1}: {e.response?.choiceId} · {e.card.choices.find((c) => c.id === e.response?.choiceId)?.label}
        </p>
      ))}
      <EventCard key={event.eventInstanceId} event={event} teamId={data.team.team.id} forecastCommitted respondToEvent={respond} kind="paused" position={positionIn(sitting, event)} limits={limitsOf(data.team, data.catalog)} risks={context.data?.anchors?.riskyChoices} solo almHref={`/c/${data.team.cohort.id}/financials?tab=risk.alm`} continueYear={{ label: resuming ? "Resuming the year…" : "Continue the year →", onClick: () => void proceed(), disabled: left > 0 || resuming, why, testId: "solo.continue" }} />
    </div>
  );
}

function Reviewed({ api, data, state }: { readonly api: ApiClient; readonly data: Ready; readonly state: SoloRunState }) {
  const round = state.round;
  const fy = fiscalYearLabel(data.catalog, round.roundNo);
  // Only this round's own report is shown, never last year's still cached.
  const results = useResultsData(state.runId, round.roundNo);
  if (results.status === "error") return <GxErrorBanner error={results.error} />;
  if (results.status !== "ready") {
    return (
      <div data-testid="solo.reviewing.loading">
        <div className="gx-eyebrow">Round {round.roundNo} of {data.team.cohort.roundCount}</div>
        <h1 className="gx-h1">Your {fy} results</h1>
        <GxLoading words="Running your year…" height={176} />
      </div>
    );
  }
  // The released year is the Results page itself (one title, one set of buttons, with Continue where the run offers it), not a copy of it.
  return <div data-testid="solo.result"><ResultsPage cohortId={state.runId} roundNo={round.roundNo} api={api} /></div>;
}
